import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef, useEffect } from 'react'
import { toast } from '@atrvd/saas-ui'
import { getSupabaseClient } from '@/lib/supabase'
import { getSupabaseUrl } from '@/lib/runtime-env'
import { createTenantDb } from '@/lib/db-tenant'
import type { TenantLedgerSyncConnection } from '@/lib/db-tenant'
import { useOrganization } from '@/contexts/OrganizationContext'
import { clearLedgerSyncOAuthStateFor, setLedgerSyncOAuthState } from '@/lib/ledgersync-oauth'
import { lsDebug, lsDebugError } from '@/lib/ledgersync-debug'
import { BANKING_KEYS } from '@/hooks/useBanking'
import type { LedgerSyncSyncPipeline } from '@dcp/shared/ledgersync/sync-state'
import { rememberSyncClick } from '@/lib/ledgersync-sync-clicks'
import { markSyncDataStale } from '@/lib/ledgersync-sync-invalidation'

/**
 * Query keys for LedgerSync v3 data (DCP-216). All keys include organizationId
 * to keep the React Query cache isolated per tenant.
 */
export const LEDGERSYNC_KEYS = {
  /** Institution catalog search — keyed by the trimmed search query. */
  institutionSearch: (organizationId: string | undefined, query: string) =>
    ['ledgersync', 'institutions', organizationId, query] as const,
  /** Org-wide connection list — drives the Bank Connections section (DCP-324). */
  connections: (organizationId: string | undefined) =>
    ['ledgersync', 'connections', organizationId] as const,
  connectionStatus: (organizationId: string | undefined, connectionId: string) =>
    ['ledgersync', 'connection-status', organizationId, connectionId] as const,
}

/**
 * Terminal v3 connection states — polling stops once one of these is reached.
 * Mirrors the CHECK-constrained status set on tenant `ledgersync_connections`.
 */
const TERMINAL_STATUSES = ['active', 'failed', 'disconnected'] as const

/**
 * Stop-poll statuses for a RECONNECTING row (DCP-173, AC.6). A row enters the
 * reconcile poll for reconnect precisely because its stored status is already
 * `failed`/`disconnected` (terminal) — so treating those as stop conditions (as
 * {@link TERMINAL_STATUSES} does) halts the poll after a single request and the
 * row never advances. In reconnect mode ONLY `active` stops the poll (the
 * successful re-link); `failed`/`disconnected` keep polling until the status
 * changes or the {@link RECONCILE_MAX_ATTEMPTS} hard cap is hit.
 */
const RECONNECT_STOP_STATUSES = ['active'] as const

/** Poll interval (ms) while a connection is still resolving in the widget. */
const STATUS_POLL_INTERVAL_MS = 3_000

/** Fast-poll interval (ms) used for the first few self-reconcile attempts. */
const RECONCILE_FAST_INTERVAL_MS = 3_000
/** Slow-poll interval (ms) after the fast burst is exhausted. */
const RECONCILE_SLOW_INTERVAL_MS = 30_000
/** Number of attempts that use the fast interval before switching to slow. */
const RECONCILE_FAST_ATTEMPTS = 5
/** Hard cap on total reconcile attempts per connection before polling stops. */
const RECONCILE_MAX_ATTEMPTS = 15

/**
 * A v3 institution catalog row as returned by the `search-institutions` edge
 * action. Mirrors the shared `LedgerSyncInstitution` shape from the edge
 * client; redeclared here so the web app does not import from `supabase/`.
 */
export interface LedgerSyncInstitution {
  /** v3 catalog id (e.g. `ins_…`) — passed to create-widget. */
  id: string
  name: string
  /** Source v3 will route to (FINICITY / MX / FDE); null when unknown. */
  source: string | null
  /** Whether the bank uses an OAuth sign-in handoff inside the widget. */
  oauthSupported: boolean
}

/** Result of the `create-widget` edge action (the v3 hosted widget hand-off). */
interface CreateWidgetResult {
  /** Tenant `ledgersync_connections.id` of the pending connection. */
  connectionId: string
  /** Hosted Connect widget URL the browser is redirected to. */
  widgetUrl: string | null
  /** ISO expiry for the widget URL, when present. */
  expiresAt: string | null
}

/** Connection status payload returned by the `connection-status` edge action. */
interface ConnectionStatus {
  status: 'initiated' | 'requires_action' | 'active' | 'failed' | 'disconnected' | string
  source: string | null
}

/** The LedgerSync Edge Functions the web app talks to (both action-routed). */
type LedgerSyncEdgeFunction = 'ledgersync-link' | 'ledgersync-sync'

/**
 * Invoke a LedgerSync Edge Function with an authenticated request. Centralises
 * session auth, the runtime Supabase URL, and the `{ data } | { error }`
 * envelope handling shared by every action either function exposes — so the
 * connect flow (`ledgersync-link`) and the transaction sync (`ledgersync-sync`,
 * DCP-173) don't duplicate the fetch/error plumbing.
 *
 * @param functionName - Which edge function to POST to.
 * @param body - The action payload (`{ action, ... }`) sent as JSON.
 * @returns The `data` envelope the edge function wraps its result in.
 */
async function invokeLedgerSyncEdge<T>(
  functionName: LedgerSyncEdgeFunction,
  body: Record<string, unknown>,
): Promise<T> {
  const supabase = getSupabaseClient()
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session?.access_token) {
    throw new Error('Authentication required')
  }

  // Log the outgoing action. The access token is intentionally omitted.
  // debug: bank-connection flow trace (DCP-216)
  lsDebug(`request:${body.action}`, { action: body.action })

  // Runtime Supabase URL (window.__ENV__ on prod / config.js), NOT build-time
  // import.meta.env — the latter is undefined in the prod image and produced a
  // broken relative URL → 405 from the frontend nginx. Mirrors usePlaid.
  const response = await fetch(
    `${getSupabaseUrl()}/functions/v1/${functionName}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(body),
    },
  )

  let json: { data?: T; error?: string }
  try {
    json = await response.json()
  } catch {
    // Non-JSON body (e.g. an HTML gateway error page) — surface a clear message.
    throw new Error(`Bank connection request failed (HTTP ${response.status})`)
  }

  if (!response.ok) {
    // debug: bank-connection flow trace (DCP-216)
    lsDebugError(`response:${body.action}`, { status: response.status, error: json?.error })
    throw new Error(json.error || `Bank connection request failed (HTTP ${response.status})`)
  }

  // debug: bank-connection flow trace (DCP-216)
  lsDebug(`response:${body.action}`, { ok: response.ok, status: response.status, data: json?.data })
  return json.data as T
}

/**
 * Invoke the `ledgersync-link` Edge Function (v3 connect flow) with an
 * authenticated request. Thin wrapper over {@link invokeLedgerSyncEdge}.
 *
 * @param body - The action payload (`{ action, ... }`) sent as JSON.
 * @returns The `data` envelope the edge function wraps its result in.
 */
async function callLedgerSyncLink<T>(body: Record<string, unknown>): Promise<T> {
  return invokeLedgerSyncEdge<T>('ledgersync-link', body)
}

/**
 * Invoke the `ledgersync-sync` Edge Function (transaction sync, DCP-173) with
 * an authenticated request. Thin wrapper over {@link invokeLedgerSyncEdge}.
 *
 * @param body - The action payload (`{ action, ... }`) sent as JSON.
 * @returns The `data` envelope the edge function wraps its result in.
 */
async function callLedgerSyncSync<T>(body: Record<string, unknown>): Promise<T> {
  return invokeLedgerSyncEdge<T>('ledgersync-sync', body)
}

/** Result of the `delete-connection` edge action (DCP-324 AC#7). */
export interface DeleteLedgerSyncConnectionResult {
  /** Always true on a 2xx — the local rows are gone. */
  deleted: boolean
  /** Whether the bank link was actually revoked at LedgerSync. */
  revoked: boolean
  /**
   * Why the revoke failed, when it did. `revoked: false` with a NULL
   * `revokeError` is the skip case (there was nothing to revoke); a non-null
   * message means the link may still be live at the aggregator even though the
   * connection is gone here — the one case the user is told about.
   */
  revokeError: string | null
}

/**
 * Delete a bank connection (AC#7): revoke the link at LedgerSync, then remove
 * what it owns locally — HARD-delete the `ledgersync_connections` row, soft-delete
 * its `bank_accounts`, hard-delete those accounts' `balances`, KEEP the
 * transactions.
 *
 * WHY THE TWO HALVES DIFFER. The connection row carries nothing worth keeping
 * once the link is revoked, and AC#7 makes the delete terminal — so it goes, and
 * the vendor id goes with it, which is what lets the user reconnect the same
 * bank cleanly. The ACCOUNTS must stay as tombstones: every money read
 * (Bank Balance, Cash Position, the LOC functions) excludes a deleted account by
 * its own `deleted_at`, and the kept transactions still point at those rows.
 *
 * WHY THIS IS AN EDGE CALL AND NOT A `db-tenant` WRITE, unlike every other
 * mutation on the connection screens. Deleting a connection has a REMOTE half:
 * the user's consent has to be withdrawn at the aggregator, which needs the
 * LedgerSync API key — and a key in the browser is not a key. The local writes
 * moved with it so the two halves cannot disagree about whether the bank is
 * still linked. The revoke is best-effort server-side: a LedgerSync outage
 * never blocks the user's delete, it comes back in `revokeError`.
 *
 * Exported from this module rather than reimplemented in `useBankConnections`
 * because the edge plumbing (session token, runtime Supabase URL, `{ data } |
 * { error }` envelope) lives here and has exactly one owner.
 *
 * @param connectionId - Tenant `ledgersync_connections.id`
 * @param organizationId - Caller's org; required for platform staff (NULL profile org)
 * @returns The edge result, including any vendor-side failure to surface
 */
export async function deleteLedgerSyncConnection(
  connectionId: string,
  organizationId: string,
): Promise<DeleteLedgerSyncConnectionResult> {
  // debug: bank-connection flow trace (DCP-324)
  lsDebug('delete-connection:start', { connectionId })
  try {
    const result = await callLedgerSyncLink<DeleteLedgerSyncConnectionResult>({
      action: 'delete-connection',
      connection_id: connectionId,
      organization_id: organizationId,
    })
    // `revoked` / `revokeError` are traced, not just `deleted`: this is the most
    // destructive action on these screens AND the only one with a half that can
    // fail without failing the call. A `revoked: false` with a message means our
    // row is gone while the bank link may still be live at the aggregator —
    // still refreshing, still billable, consent not actually withdrawn — and by
    // then the row is unreachable from the UI, so this trace is the only place
    // that outcome can be reconstructed from the user's own session.
    // debug: bank-connection flow trace (DCP-324)
    lsDebug('delete-connection:result', {
      connectionId,
      deleted: result.deleted,
      revoked: result.revoked,
      revokeError: result.revokeError,
    })
    return result
  } catch (e) {
    // Re-thrown untouched: the caller's mutation surfaces the raw edge message.
    // debug: bank-connection flow trace (DCP-324)
    lsDebugError('delete-connection:error', {
      connectionId,
      error: e instanceof Error ? e.message : String(e),
    })
    throw e
  }
}

/**
 * Search the v3 institution catalog by name. Exposed as a mutation so the
 * connect dialog can fire it imperatively on each (debounced) keystroke and
 * read `data` / `isPending` for the results list and spinner. Errors are
 * surfaced via the caller (the dialog shows an inline message).
 */
export function useSearchLedgerSyncInstitutions() {
  return useMutation({
    mutationFn: async (query: string) => {
      const trimmed = query.trim()
      if (trimmed.length === 0) return [] as LedgerSyncInstitution[]
      const { institutions } = await callLedgerSyncLink<{ institutions: LedgerSyncInstitution[] }>(
        { action: 'search-institutions', query: trimmed },
      )
      // debug: bank-connection flow trace (DCP-216)
      lsDebug('search-institutions:result', { count: institutions.length })
      return institutions
    },
  })
}

/**
 * List every LedgerSync connection for the current organization, newest first.
 *
 * Drives the org-level Bank Connections section (DCP-324): it takes no
 * facility argument and uses an org-scoped query key, since a connection is an
 * organization-level object whose accounts may be spread over any number of
 * facilities.
 */
export function useLedgerSyncConnections() {
  const { currentOrganization } = useOrganization()
  return useQuery<TenantLedgerSyncConnection[]>({
    queryKey: LEDGERSYNC_KEYS.connections(currentOrganization?.id),
    queryFn: async () => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.ledgerSyncConnections.list()
      if (error) throw new Error(error.message)
      // debug: bank-connection flow trace (DCP-216)
      lsDebug('connections:result', { count: data.length })
      return data
    },
    enabled: !!currentOrganization?.id,
  })
}

/**
 * Create a v3 Connect widget session for a chosen institution.
 *
 * On success it persists the pending-connect state to localStorage (so the
 * redirect-based widget callback knows which connection to poll and where to
 * return) and resolves with `{ connectionId, widgetUrl, expiresAt }` — the
 * caller redirects the browser to `widgetUrl`.
 *
 * NO `facility_id` IS SENT (DCP-324). A connection is ORG-level now: the column
 * is gone from `ledgersync_connections`, the facility lives on each
 * `bank_accounts` row, and the edge function's `create-widget` action no longer
 * reads the field. Keeping it in the payload would state something about the
 * connection that is not true and would outlive the ticket as the reason
 * somebody reintroduces a facility scope.
 *
 * `returnPath` is where the widget's redirect should land the user. It defaults
 * to Banking Settings, which is where the connect flow lives per AC#2.
 */
export function useCreateLedgerSyncWidget() {
  const { currentOrganization } = useOrganization()
  return useMutation({
    mutationFn: async (params: {
      institutionId: string
      organizationId?: string
      /** Route to return to after the widget redirect. Defaults to Banking Settings. */
      returnPath?: string
    }): Promise<CreateWidgetResult> => {
      const result = await callLedgerSyncLink<CreateWidgetResult>({
        action: 'create-widget',
        institution_id: params.institutionId,
        organization_id: params.organizationId ?? currentOrganization?.id,
      })

      // debug: bank-connection flow trace (DCP-216)
      lsDebug('create-widget:result', {
        connectionId: result.connectionId,
        hasWidgetUrl: !!result.widgetUrl,
        expiresAt: result.expiresAt,
      })

      // Persist the redirect-survival state before the caller navigates away.
      setLedgerSyncOAuthState({
        connectionId: result.connectionId,
        returnPath: params.returnPath ?? '/settings/financial-accounts',
      })

      return result
    },
  })
}

/** Result of the `reauthorize` edge action (DCP-324 "Reconnect"). */
export interface ReauthorizeConnectionResult {
  /** Tenant `ledgersync_connections.id` — the row being repaired. */
  connectionId: string
  /** Hosted re-consent page the browser is redirected to. */
  reauthUrl: string
  /** ISO expiry of the URL (~6 days out); null when the vendor omitted it. */
  expiresAt: string | null
  /** Provider the URL leads to (FINICITY / MX / FDE). */
  source: string | null
  /**
   * True when LedgerSync routed the user to a DIFFERENT provider than the one
   * the connection is on — see {@link useReauthorizeConnection} for what we do
   * about it and why.
   */
  sourceChanged: boolean
}

/**
 * Re-consent at an EXISTING bank connection — the "Reconnect" action (DCP-324).
 *
 * WHY THIS EXISTS RATHER THAN REUSING {@link useCreateLedgerSyncWidget}, which
 * is what Reconnect used to call. `create-widget` starts a BRAND NEW connection:
 * the product owner reproduced it — clicking Reconnect and entering the same
 * bank credentials produced a SECOND connection to the same bank, with new
 * `con_`/`acc_`/`txn_` ids, duplicating the accounts and defeating the
 * `ledgersync_transaction_id` dedupe on the next delta pull. The `reauthorize`
 * action repairs the existing connection, so its ids stay stable.
 *
 * IT IS NOT LIMITED TO BROKEN CONNECTIONS. The vendor endpoint is explicitly not
 * gated on status, and re-running it on an ACTIVE connection re-opens the bank's
 * account-selection step — on FINICITY that is the only supported way to pick up
 * an account the user opened after linking. Callers must not hide it behind a
 * status check.
 *
 * ON SUCCESS the redirect-survival state is persisted before the caller
 * navigates, exactly as `useCreateLedgerSyncWidget` does, so the callback page
 * knows which row to poll and where to return the user.
 *
 * EXCEPT ON THE `sourceChanged` BRANCH, where we deliberately do NOT persist it
 * and the caller must NOT redirect. LedgerSync occasionally serves a bank
 * exclusively through a different provider than the one the connection is on
 * (Finicity connections migrated to MX; American Express is on that list in
 * production), and on that branch the URL it minted is an ADD-BANK link for the
 * new provider, not a repair link: completing it creates a new connection with
 * new ids and leaves this one untouched. Following it would therefore produce
 * exactly the duplicate this hook exists to prevent, and worse — the new
 * connection is born outside our connect flow, so it has no tenant row, no
 * webhook-routing registration and nothing to finalise it, while the poll
 * pinned to the old row would watch a connection that cannot move and time out
 * on a flow the user actually completed. So we stop and tell the user to use
 * "Connect Bank", which is the supported way to create a new connection and the
 * path that does all of that bookkeeping.
 */
export function useReauthorizeConnection() {
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (params: {
      /** Tenant `ledgersync_connections.id` the user clicked Reconnect on. */
      connectionId: string
      /** Route to return to after the redirect. Defaults to Banking Settings. */
      returnPath?: string
    }): Promise<ReauthorizeConnectionResult> => {
      const result = await callLedgerSyncLink<ReauthorizeConnectionResult>({
        action: 'reauthorize',
        connection_id: params.connectionId,
        organization_id: currentOrganization?.id,
      })

      // debug: bank-connection flow trace (DCP-324)
      // The URL is never logged: it is a reusable credential that opens the
      // end-user's bank-linking session until `expiresAt` (~6 days).
      lsDebug('reauthorize:result', {
        connectionId: result.connectionId,
        source: result.source,
        sourceChanged: result.sourceChanged,
        expiresAt: result.expiresAt,
      })

      // Only the stable branch gets redirect-survival state (see the doc block).
      if (!result.sourceChanged) {
        setLedgerSyncOAuthState({
          connectionId: result.connectionId,
          returnPath: params.returnPath ?? '/settings/financial-accounts',
        })
      }

      return result
    },
    onSuccess: (result) => {
      if (result.sourceChanged) {
        // Not a redirect and not a silent no-op: the user asked to reconnect and
        // must learn why they are not being sent to their bank, and what to do
        // instead. `toast.error` rather than a warning because this is a dead end
        // for the action they took.
        toast.error(
          'This bank has moved to a different provider, so reconnecting would create a duplicate connection. Use "Connect Bank" to link it again, then remove this connection.',
        )
        return
      }
      // Refresh the lists that render the row's status before the browser leaves
      // the page, so returning from the hosted page (or cancelling and coming
      // back) shows current state rather than the pre-reconnect snapshot.
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBankConnections })
      queryClient.invalidateQueries({ queryKey: LEDGERSYNC_KEYS.connections(currentOrganization?.id) })
    },
    onError: (error, params) => {
      // debug: bank-connection flow trace (DCP-324)
      lsDebugError('reauthorize:error', {
        connectionId: params.connectionId,
        error: error instanceof Error ? error.message : String(error),
      })
      // The edge action's refusals are written for the user (e.g. a connection
      // that never finished linking is told to connect the bank again), so the
      // raw message is the most useful thing to show.
      toast.error(error instanceof Error ? error.message : 'Failed to start reconnect')
    },
  })
}

/**
 * Poll a connection toward a terminal state after the widget session completes.
 * Each call hits the `connection-status` action, which re-reads the live v3
 * connection and syncs status/source onto the tenant row. Disable via
 * `enabled`; polling self-stops once the status is terminal.
 *
 * NOTE: the connection is finalized by active polling — not by a webhook
 * delivery. Sandbox does not reliably deliver `connection.active`, and MX-
 * sourced widgets do not redirect back; polling is therefore the primary signal.
 *
 * @param connectionId - Tenant connection row id to poll, or null when none.
 * @param enabled - Gate that lets the callback page pause polling on error.
 */
export function useLedgerSyncConnectionStatus(connectionId: string | null, enabled: boolean) {
  const { currentOrganization } = useOrganization()
  return useQuery<ConnectionStatus>({
    queryKey: LEDGERSYNC_KEYS.connectionStatus(currentOrganization?.id, connectionId ?? ''),
    queryFn: async () => {
      const result = await callLedgerSyncLink<ConnectionStatus>({
        action: 'connection-status',
        connection_id: connectionId,
        organization_id: currentOrganization?.id,
      })
      // debug: bank-connection flow trace (DCP-216)
      lsDebug('status:poll', { connectionId, status: result.status })
      return result
    },
    enabled: enabled && !!connectionId && !!currentOrganization?.id,
    // Poll while resolving; stop once a terminal status is reached.
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status && TERMINAL_STATUSES.includes(status as (typeof TERMINAL_STATUSES)[number])) {
        return false
      }
      return STATUS_POLL_INTERVAL_MS
    },
  })
}

/**
 * Self-reconcile a single pending LedgerSync connection from the Bank
 * Connections section, without relying on the post-Connect redirect or a
 * webhook delivery.
 *
 * WHY this exists: MX-sourced connections do not redirect back to our
 * `redirect_url` after the widget closes — the widget simply dismisses. Sandbox
 * webhook delivery for `connection.active` is also unreliable, so the section
 * cannot depend on either signal to flip a pending connection to active.
 * Instead, each pending row mounts one instance of this hook, which polls the
 * `connection-status` edge action (which re-reads the live v3 state and syncs
 * it server-side) until the connection settles.
 *
 * BACKOFF: the first `RECONCILE_FAST_ATTEMPTS` (5) polls fire every
 * `RECONCILE_FAST_INTERVAL_MS` (3 s); subsequent polls use
 * `RECONCILE_SLOW_INTERVAL_MS` (30 s). Polling stops unconditionally after
 * `RECONCILE_MAX_ATTEMPTS` (15) total attempts, or immediately when the
 * connection reaches a terminal status. On terminal resolution the org-level
 * connection list is invalidated so the UI re-fetches the up-to-date row.
 *
 * NOTE: `useLedgerSyncConnectionStatus` (used by the redirect callback page)
 * is intentionally left unchanged — it has its own fixed 3 s interval.
 *
 * @param connectionId - Tenant `ledgersync_connections.id` to reconcile, or
 *   null when disabled (nothing is fetched when null).
 * @param enabled - External gate; set to false to suppress all polling.
 * @param reconnecting - When true, poll in reconnect mode: only `active` stops
 *   the poll (see {@link RECONNECT_STOP_STATUSES}), so a row whose stored status
 *   is already `failed`/`disconnected` keeps polling toward a successful re-link
 *   instead of stopping after one request (DCP-173, AC.6).
 * @returns The underlying React Query result (status, data, etc.).
 */
export function useReconcileLedgerSyncConnection(
  connectionId: string | null,
  enabled: boolean,
  reconnecting = false,
) {
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()

  // Which statuses end the poll: `active` only while reconnecting, the full
  // terminal set otherwise (DCP-173, AC.6). Both operands are module constants,
  // so this ref is stable per `reconnecting` value.
  const stopStatuses: readonly string[] = reconnecting
    ? RECONNECT_STOP_STATUSES
    : TERMINAL_STATUSES

  /**
   * Tracks how many query executions have fired for this hook instance.
   * Stored in a ref so it does not trigger re-renders and is stable across
   * React Query's internal refetch cycles.
   */
  const attemptsRef = useRef(0)

  const query = useQuery<ConnectionStatus>({
    queryKey: LEDGERSYNC_KEYS.connectionStatus(currentOrganization?.id, connectionId ?? ''),
    queryFn: async () => {
      // Increment before the fetch so the refetchInterval callback below sees
      // the post-fetch count when deciding the next interval.
      attemptsRef.current += 1
      // The last attempt of a RECONNECT poll consumes the redirect-survival
      // state: a later mount must not restart a poll that already ran out (see
      // the terminal effect, including why a first-connect poll never clears).
      if (reconnecting && attemptsRef.current >= RECONCILE_MAX_ATTEMPTS && connectionId) {
        clearLedgerSyncOAuthStateFor(connectionId)
      }
      const result = await callLedgerSyncLink<ConnectionStatus>({
        action: 'connection-status',
        connection_id: connectionId,
        organization_id: currentOrganization?.id,
      })
      // debug: bank-connection flow trace (DCP-216)
      lsDebug('reconcile:poll', {
        connectionId,
        attempt: attemptsRef.current,
        status: result.status,
        source: result.source,
      })
      return result
    },
    enabled: enabled && !!connectionId && !!currentOrganization?.id,
    // Prevent browser focus events from triggering extra polls that would
    // throw off the attempt counter and the intended backoff cadence.
    refetchOnWindowFocus: false,
    /**
     * Dynamic interval implementing the fast-then-slow backoff:
     * - Stop immediately on terminal status.
     * - Stop if the hard cap has been reached.
     * - Use the fast interval for the first RECONCILE_FAST_ATTEMPTS fetches,
     *   then fall back to the slow interval.
     */
    refetchInterval: (q) => {
      const status = q.state.data?.status
      if (status && stopStatuses.includes(status)) {
        return false
      }
      if (attemptsRef.current >= RECONCILE_MAX_ATTEMPTS) {
        return false
      }
      return attemptsRef.current < RECONCILE_FAST_ATTEMPTS
        ? RECONCILE_FAST_INTERVAL_MS
        : RECONCILE_SLOW_INTERVAL_MS
    },
  })

  /**
   * When the connection settles into a terminal status, invalidate every list
   * that renders the row so it re-fetches the finalized connection (institution
   * name, status, …) and the freshly-pulled accounts (which only exist
   * server-side once the connection becomes active).
   *
   * A connection is an ORGANIZATION-level object since DCP-324, so this only
   * ever refreshes the org-level `BANKING_KEYS` caches behind the Bank
   * Connections section — there is no facility-scoped list left to invalidate
   * (the org-level Bank Connections section is this hook's only mounting
   * point).
   */
  useEffect(() => {
    const status = query.data?.status
    if (status && stopStatuses.includes(status)) {
      // debug: bank-connection flow trace (DCP-216)
      lsDebug('reconcile:terminal', { connectionId, status })
      // A RECONNECT poll consumes the redirect-survival state of its own
      // connection; `BankConnectionsSection` would otherwise resume polling it
      // on every mount. Scoped to this id, and a no-op if the callback page
      // cleared it. A first-connect poll never touches it: the state is shared
      // across tabs, and the tab still in the widget needs it for its callback
      // page — clearing it there reads "No connection in progress was found" and
      // invites a second connection to the same bank (ADR 0011).
      if (reconnecting && connectionId) clearLedgerSyncOAuthStateFor(connectionId)
      // DCP-324: the org-level connection list. Invalidated by PREFIX, never by
      // `bankConnections(orgId, column, direction)` — that list is keyed by its
      // sort, so an exact key would miss every entry the user has actually
      // sorted and the finalized row would keep reading "Connecting…" until the
      // staleTime expired. Unconditional on the terminal status: `status` and
      // `last_error` change on failure too, and both are columns of that list.
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBankConnections })
      // The raw org-wide connections list. NOTE: as of the DCP-324 reauthorize
      // switch this key has no reader left — `BankConnectionsSection` used to
      // pull it for each row's `institution_id`, which only Reconnect needed,
      // and Reconnect now works off the stored canonical id. The invalidation is
      // kept because `useLedgerSyncConnections` and the key still exist, so a
      // future reader would otherwise silently start life with a stale cache.
      // Remove all three together or not at all.
      queryClient.invalidateQueries({
        queryKey: LEDGERSYNC_KEYS.connections(currentOrganization?.id),
      })
      // DCP-260: a LedgerSync account IS a `bank_accounts` row (DCP-217
      // collapse). The org-wide `bank_accounts` caches keyed under
      // ['banking','bank-accounts'] are a different key space from
      // LEDGERSYNC_KEYS, so without this the Settings → Facilities "Bank
      // Accounts" count (and the Dashboard account filter) keep the pre-connect
      // number for the full 5-minute staleTime.
      //
      // Narrowed to 'active' — NOT every terminal status — because those rows are
      // materialised in exactly one place, `finalizeConnection`
      // (supabase/functions/_shared/ledgersync-finalize.ts), which the
      // connection-status poll only reaches on its `nextStatus === 'active'`
      // branch; the `connection.failed` / `connection.disconnected` webhook writes
      // `ledgersync_connections` alone. So a failed or cancelled connect attempt
      // creates nothing to re-count, and invalidating there would refetch an
      // org-wide list (max_rows = 1000) for no visible change — the same cost
      // that took this call back out of the sync hooks below (PResto 54975 #6).
      //
      // This hook is NOT the only terminal handler of this transition: the
      // OAuth-redirect flow lands on `LedgerSyncCallbackPage` instead, where
      // this hook may never mount. That page carries the same invalidation,
      // under the same 'active'-only condition — both must keep it, or the flow
      // that skips this one goes stale (PResto 54957 #1).
      if (status === 'active') {
        queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBankAccounts })
        // DCP-324: the per-connection accounts tables on the connection detail
        // screen. Also a PREFIX invalidation — every entry under it carries a
        // filter+sort+cursor tail that this hook has no way to enumerate.
        // Narrowed to 'active' for the same reason as `allBankAccounts` above:
        // the account rows are materialised only by `finalizeConnection`, which
        // the poll reaches on its `nextStatus === 'active'` branch alone.
        queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allConnectionAccounts })
        // DCP-374: same reasoning one step further — the card page reads those
        // same account rows, and its terms columns are written by the finalize
        // this branch is waiting for.
        queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allCreditCardAvailability })
      }
    }
  }, [
    query.data?.status,
    currentOrganization?.id,
    queryClient,
    connectionId,
    stopStatuses,
    reconnecting,
  ])

  return query
}

/** Result of the `sync-connection` edge action — the triggered Trigger.dev run. */
interface SyncConnectionResult {
  /** Trigger.dev run id for the launched transaction-sync job. */
  runId: string
  /**
   * Trigger.dev run id for the balance refresh the same call launched, when the
   * caller asked for one via `include_balances` (DCP-397). NULL whenever the
   * edge enqueued no balance job — which is every call that does not set the
   * flag, {@link useTriggerLedgerSyncSyncAllOrg} included.
   *
   * Read only as a presence flag (DCP-401): non-NULL means the balance half
   * was enqueued, so its finish is worth waiting for and toasting. The run id
   * itself is never polled — the screens follow both halves through the
   * per-pipeline status on the connection row.
   */
  balanceRunId: string | null
  /**
   * DCP-401: the idempotency key matched an EXISTING transaction run (a second
   * press inside the same minute) — no new sync started, so there is nothing
   * of this click's own to wait for. Absent from an older edge: read as false.
   */
  deduplicated?: boolean
  /** Same, for the balance half. */
  balanceDeduplicated?: boolean
}

/**
 * Result of the `sync-balances-connection` edge action (DCP-324, AC#25) — the
 * balance force-refresh of ONE bank login. Connection-scoped, so it returns a
 * single Trigger.dev run id rather than a fan-out envelope, exactly like the
 * transaction `sync-connection` action.
 */
interface SyncBalancesConnectionResult {
  /** Trigger.dev run id for the launched balance-refresh job. */
  runId: string
  /** DCP-401: an existing run was returned instead of a new one started. */
  deduplicated?: boolean
}

/**
 * Result of the `sync-balance-account` edge action (DCP-217). This action runs
 * SYNCHRONOUSLY (a single `GET /accounts/{id}` v3 read + write), so the edge
 * returns the freshly-applied balance in the response — no Trigger.dev job, no
 * polling. All fields optional so logging stays defensive.
 */
interface SyncBalanceAccountResult {
  /** Always 'success' on a 2xx (failures come back as an error response). */
  status?: string
  /** The balance just written, echoed back from LedgerSync. */
  balance?: number | null
  availableBalance?: number | null
  currency?: string | null
  lastRefreshedAt?: string | null
}

/**
 * Trigger a balance read for a SINGLE connected bank account (DCP-217 — the
 * per-row "Sync"/"Resync" button on the Balance Import table).
 *
 * Invokes the `ledgersync-sync` edge `sync-balance-account` action with the
 * bank account id. This action is SYNCHRONOUS — the edge does the LedgerSync
 * read + DB write before responding, so by the time we invalidate on success
 * the fresh balance is already persisted and the refetch shows it immediately
 * (no polling / manual refresh). We invalidate the Balance Import status query
 * (row status/timestamp/source) and the banking KPI query (aggregated "Bank
 * Balance", AC#2). The mutation `variables` carries the in-flight
 * `bankAccountId`, which the table uses to spin ONLY the clicked row's button
 * for the full read+write duration.
 */
export function useTriggerLedgerSyncSyncBalanceAccount() {
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (bankAccountId: string) => {
      // debug: bank-connection flow trace (DCP-216)
      lsDebug('sync-balance-account:start', { bankAccountId })
      return callLedgerSyncSync<SyncBalanceAccountResult>({
        action: 'sync-balance-account',
        bank_account_id: bankAccountId,
        // Platform-staff (super_admin etc.) have no fixed organization_id, so the
        // edge function requires it explicitly — mirror the other LedgerSync calls.
        organization_id: currentOrganization?.id,
      })
    },
    onSuccess: (result, bankAccountId) => {
      // debug: bank-connection flow trace (DCP-216)
      lsDebug('sync-balance-account:done', { bankAccountId, balance: result.balance })
      // The write already happened server-side, so this refetch shows the fresh
      // balance right away: re-read the unified Balance Import table
      // (status/timestamp/source) and recompute the aggregated Bank Balance
      // tile (DCP-217, AC#2/#5).
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBalanceImportStatus })
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allKpi })
      // DCP-374: and the card page, because this same write carries the terms.
      // The edge action spreads `cardTermsFields(acct)` into the bank_accounts
      // update, so a resync moves the limit, the statement balance and the due
      // date — and Availability, which is limit + balance, is recomputed from
      // them. Without this the toast says "Balance updated" while the Credit
      // Card Availability page keeps serving pre-sync figures for the whole
      // 5-minute staleTime. The one SYNCHRONOUS writer of card terms, so the
      // stale window here is visible immediately rather than racing a task
      // (PR #238 review).
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allCreditCardAvailability })
      // Synchronous read → the balance is already updated (not a "started" hint).
      toast.success('Balance updated')
    },
    onError: (error, bankAccountId) => {
      // debug: bank-connection flow trace (DCP-216)
      lsDebugError('sync-balance-account:error', {
        bankAccountId,
        error: error instanceof Error ? error.message : String(error),
      })
      // Synchronous op ("Sync" button) — not an async job start, so the copy
      // says "sync", not "start ... sync" (DCP-217 review #4).
      toast.error(error instanceof Error ? error.message : 'Failed to sync balance')
    },
  })
}

// ===========================================================================
// DCP-324 — org-level sync fan-out (Bank Connections, Screen 1)
// ===========================================================================

/**
 * Outcome of one org-level fan-out: how many jobs were enqueued and which
 * targets refused.
 */
export interface OrgSyncFanOutResult {
  /** Targets a job was successfully enqueued for. */
  succeeded: number
  /** Targets whose trigger call failed, with the reason. */
  failures: Array<{ id: string; message: string }>
  /**
   * Targets the edge answered with an EXISTING run (DCP-401) — the idempotency
   * key matched, so nothing new started for them. Counted in `succeeded`: the
   * request did succeed, it just had nothing to add.
   */
  deduplicated: string[]
}

/**
 * Run a fan-out over targets, collecting per-target failures instead of
 * aborting on the first one.
 *
 * `Promise.allSettled`, not a loop with `await` inside: the calls are
 * independent edge invocations and one slow bank must not delay the rest. One
 * bad target (a connection that went `disconnected` between the list render and
 * the click, which the edge answers 409) therefore costs that bank its sync
 * and nothing else.
 *
 * @param ids - Targets to trigger
 * @param trigger - Per-target edge call
 * @returns Counts + the failures, for the caller's toast
 */
async function fanOutSync(
  ids: string[],
  trigger: (id: string) => Promise<{ deduplicated?: boolean }>,
): Promise<OrgSyncFanOutResult> {
  const settled = await Promise.allSettled(ids.map((id) => trigger(id)))
  const failures: Array<{ id: string; message: string }> = []
  const deduplicated: string[] = []
  let succeeded = 0
  settled.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      succeeded++
      if (result.value.deduplicated === true) deduplicated.push(ids[index])
      return
    }
    const reason = result.reason
    failures.push({
      id: ids[index],
      message: reason instanceof Error ? reason.message : String(reason),
    })
  })
  return { succeeded, failures, deduplicated }
}

/**
 * The targets of a fan-out that a NEW run was started for (DCP-401) — every
 * id passed in minus the ones the edge refused and the ones it answered with
 * an existing run. Only these are worth waiting for: a deduplicated target's
 * run is already followed by the click that started it, or has finished.
 *
 * @param ids - The fan-out's targets
 * @param result - Its outcome
 */
function startedTargets(ids: string[], result: OrgSyncFanOutResult): string[] {
  const notStarted = new Set([...result.failures.map((failure) => failure.id), ...result.deduplicated])
  return ids.filter((id) => !notStarted.has(id))
}

/**
 * "Sync all transactions" for the whole organization (DCP-324, AC#1).
 *
 * FANS OUT CLIENT-SIDE over the connection ids the caller passes, invoking the
 * existing `sync-connection` action once per connection. There is no `sync-org`
 * action on the `ledgersync-sync` edge function, and none is needed: the
 * fan-out is exact here — it targets precisely the connections the list shows
 * as `active`, with no facility indirection — while a server-side sweep would
 * have to re-derive that set and could not report which bank failed. The
 * balance fan-out below is the same shape for the same reason.
 *
 * Idempotency still holds per connection: the edge action derives its
 * Trigger.dev idempotency key from the connection id, so a double click cannot
 * enqueue the same connection's job twice.
 *
 * IT DELIBERATELY DOES NOT SET `include_balances` (DCP-397), unlike the
 * per-connection {@link useTriggerLedgerSyncSyncConnection} that shares this
 * action. This button says "Sync all TRANSACTIONS" and sits next to a "Sync all
 * balances" that fans out over the same connection list — folding balances in
 * here would make the pair do the same work twice and make the first button's
 * label a lie. The per-connection Sync has no such neighbour, which is the whole
 * reason the flag exists.
 *
 * @param connectionIds - Active connections to sync (the caller filters by status)
 */
export function useTriggerLedgerSyncSyncAllOrg() {
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (connectionIds: string[]): Promise<OrgSyncFanOutResult> => {
      // debug: bank-connection flow trace (DCP-324)
      lsDebug('sync-org:start', { connections: connectionIds.length })
      return fanOutSync(connectionIds, (connectionId) =>
        callLedgerSyncSync<SyncConnectionResult>({
          action: 'sync-connection',
          connection_id: connectionId,
          organization_id: currentOrganization?.id,
        }),
      )
    },
    // DCP-401: pin the organization the click was made in. `onSuccess` runs
    // after the request resolves and reads the LATEST options, so a switch of
    // organization in between would file the click under the new one.
    onMutate: () => ({ organizationId: currentOrganization?.id }),
    onSuccess: (result, connectionIds, context) => {
      // debug: bank-connection flow trace (DCP-324)
      lsDebug('sync-org:done', { succeeded: result.succeeded, failed: result.failures.length })
      // NOTHING WAS ATTEMPTED — an empty target list — is not a failure, and it
      // is not the same event as "every bank refused". Both arrive as
      // `succeeded === 0`, so a bare `succeeded === 0 → toast.error` told the
      // user something had failed when no request was ever issued. `failures`
      // is what separates them: zero targets produces zero failures.
      //
      // Returning before the invalidation is deliberate too: no job was
      // enqueued, so no status was stamped, and refetching the list to show
      // the same rows back would be pure noise.
      //
      // Today's UI hides both "Sync all" buttons entirely while no connection is
      // `active` (`showSyncAll` in BankConnectionsSection), so this is a guard
      // for the state being reached another way — a list that emptied between
      // render and click, or a future caller that forgets to filter by status —
      // not a path the screen walks.
      if (result.succeeded === 0 && result.failures.length === 0) return
      if (result.succeeded === 0) {
        // Nothing was enqueued, so no status was stamped and there is nothing
        // to follow — the refusal is the whole story.
        toast.error(
          result.failures[0]?.message ?? 'Failed to start transaction sync for any connection',
        )
        return
      }
      // DCP-401: one fan-out click owning every ACCEPTED bank's transactions
      // pipeline — the watcher reports per-bank failures from it and stays
      // quiet about successes, since this toast already said what started.
      rememberSyncClick(
        queryClient,
        context?.organizationId,
        'fan-out',
        startedTargets(connectionIds, result).map((connectionId) => ({
          connectionId,
          pipeline: 'transactions' as const,
        })),
      )
      markSyncDataStale(queryClient, ['transactions'])
      // Toasts BEFORE the refetch below: the user gets the answer to the click
      // at once, not after a list round trip. Neutral "started" hint: the jobs
      // are asynchronous, so nothing has been imported yet.
      toast.info(`Syncing ${result.succeeded} connection${result.succeeded === 1 ? '' : 's'}…`)
      if (result.failures.length > 0) {
        toast.error(
          `${result.failures.length} connection${result.failures.length === 1 ? '' : 's'} could not be synced`,
        )
      }
      // DCP-401: ONLY the connection list is refetched, and RETURNED so
      // `isPending` lasts until the rows carrying `queued` are on screen — no
      // flash of idle between the click and the poll. The DATA caches
      // (accounts, transactions, KPIs) were only MARKED stale above, so a page
      // mounted later refetches; `useLedgerSyncCompletionWatcher` does the
      // real refresh when each run finishes. Refetching them here, as this
      // used to, re-read the pre-sync rows seconds after the click.
      return queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBankConnections })
    },
    onError: (error) => {
      // debug: bank-connection flow trace (DCP-324)
      lsDebugError('sync-org:error', {
        error: error instanceof Error ? error.message : String(error),
      })
      toast.error(error instanceof Error ? error.message : 'Failed to start transaction sync')
    },
  })
}

/**
 * "Sync all balances" for the whole organization (DCP-324, AC#1/AC#25).
 *
 * FANS OUT CLIENT-SIDE over the CONNECTION ids the caller passes, invoking the
 * `sync-balances-connection` edge action once per connection — the exact mirror
 * of {@link useTriggerLedgerSyncSyncAllOrg}, and the reason that action exists.
 *
 * IT USED TO GO THROUGH ACTIVE FACILITIES (`sync-balances-facility`), which
 * cannot satisfy AC#25: that action resolves a facility's connections THROUGH
 * its accounts, so a Connected connection whose accounts are ALL unassigned or
 * all on deactivated facilities belonged to no facility's sweep and was never
 * refreshed — silently, since the fan-out still reported success. Per
 * connection the targeting is exact and needs no facility indirection.
 *
 * Idempotency still holds per connection: the edge action derives its
 * Trigger.dev key from the connection id, so a double click cannot enqueue the
 * same connection's refresh twice.
 *
 * @param connectionIds - Connected connections to refresh (the caller filters by status)
 */
export function useTriggerLedgerSyncSyncBalancesAllOrg() {
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (connectionIds: string[]): Promise<OrgSyncFanOutResult> => {
      // debug: bank-connection flow trace (DCP-324)
      lsDebug('sync-balances-org:start', { connections: connectionIds.length })
      return fanOutSync(connectionIds, (connectionId) =>
        callLedgerSyncSync<SyncBalancesConnectionResult>({
          action: 'sync-balances-connection',
          connection_id: connectionId,
          organization_id: currentOrganization?.id,
        }),
      )
    },
    // DCP-401: pin the click's organization — see useTriggerLedgerSyncSyncAllOrg.
    onMutate: () => ({ organizationId: currentOrganization?.id }),
    onSuccess: (result, connectionIds, context) => {
      // debug: bank-connection flow trace (DCP-324)
      lsDebug('sync-balances-org:done', {
        succeeded: result.succeeded,
        failed: result.failures.length,
      })
      // Empty target list = nothing attempted, which is neither a success worth
      // a toast nor a failure worth an error one; see the identical guard in
      // {@link useTriggerLedgerSyncSyncAllOrg} for the full reasoning.
      if (result.succeeded === 0 && result.failures.length === 0) return
      if (result.succeeded === 0) {
        toast.error(result.failures[0]?.message ?? 'Failed to start balance sync')
        return
      }
      // DCP-401: a fan-out click over the accepted banks' balances pipeline —
      // per-bank failures are reported from it, successes are not.
      rememberSyncClick(
        queryClient,
        context?.organizationId,
        'fan-out',
        startedTargets(connectionIds, result).map((connectionId) => ({
          connectionId,
          pipeline: 'balances' as const,
        })),
      )
      markSyncDataStale(queryClient, ['balances'])
      toast.info('Balance sync started')
      // DCP-401: refetch (and await, via the returned promise) the connection
      // list, which carries each connection's `balance_sync_status` stamped
      // `queued` by the edge, so its poll starts. The balance-derived caches
      // were only marked stale above; `useLedgerSyncCompletionWatcher`
      // refreshes them when each run finishes.
      return queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBankConnections })
    },
    onError: (error) => {
      // debug: bank-connection flow trace (DCP-324)
      lsDebugError('sync-balances-org:error', {
        error: error instanceof Error ? error.message : String(error),
      })
      toast.error(error instanceof Error ? error.message : 'Failed to start balance sync')
    },
  })
}

/**
 * Trigger a manual sync for ONE connection, org-level (DCP-324 AC#6, DCP-397).
 *
 * Invokes the `ledgersync-sync` edge `sync-connection` action with no facility
 * in scope, and refetches the org-level connection list, whose per-pipeline
 * sync status is how the screens follow the run to its end (DCP-401).
 *
 * IT ASKS FOR BALANCES TOO (`include_balances: true`, DCP-397) — the single
 * change that makes this button honest. Since DCP-397 every sync first has the
 * source re-aggregate the connection AT THE BANK and waits for it, and that
 * aggregation refreshes everything the source holds for the login, balances
 * included. Reading only the transactions out of it spent one of the vendor's
 * (undisclosed, daily-budgeted) aggregations to import half of what we paid for,
 * and left the user pressing "Sync all balances" — an org-wide sweep — to
 * collect the other half of their own connection's refresh.
 *
 * It is close to free: the refresh is deduplicated on a global key server-side,
 * so the balance job started alongside this transaction sync ATTACHES TO THE
 * SAME refresh run and reads its balances from the very snapshot the
 * transactions came from. No second aggregation, and no chance of the two halves
 * describing different moments at the bank.
 */
export function useTriggerLedgerSyncSyncConnection() {
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (connectionRowId: string) => {
      // debug: bank-connection flow trace (DCP-324)
      lsDebug('sync-connection:start', { connectionId: connectionRowId, includeBalances: true })
      return callLedgerSyncSync<SyncConnectionResult>({
        action: 'sync-connection',
        connection_id: connectionRowId,
        organization_id: currentOrganization?.id,
        // DCP-397 — see the doc block: this hook's defining difference from the
        // org-wide fan-out, which shares the action and must stay transactions-only.
        include_balances: true,
      })
    },
    // DCP-401: pin the click's organization — see useTriggerLedgerSyncSyncAllOrg.
    onMutate: () => ({ organizationId: currentOrganization?.id }),
    onSuccess: (result, connectionRowId, context) => {
      // debug: bank-connection flow trace (DCP-324, DCP-397)
      // `balanceRunId` is traced next to `runId` because it is the only evidence
      // the balance half was enqueued at all: a NULL here on a call that asked
      // for balances is the signature of a sync that moved transactions only.
      lsDebug('sync-connection:done', {
        connectionId: connectionRowId,
        runId: result.runId,
        balanceRunId: result.balanceRunId,
      })
      // DCP-401: ONE click owning the pipelines it STARTED — a half the edge
      // did not enqueue (no `balanceRunId`) or answered with an existing run
      // (`*Deduplicated`, a second press inside the idempotency minute) has no
      // new run of this click's to finish, and waiting for one would leave the
      // click open to claim some later, unrelated finish. The watcher reports
      // it once, when every member has finished ("<bank> synced", or the error).
      const started: LedgerSyncSyncPipeline[] = []
      if (!result.deduplicated) started.push('transactions')
      if (result.balanceRunId && !result.balanceDeduplicated) started.push('balances')
      rememberSyncClick(
        queryClient,
        context?.organizationId,
        'single',
        started.map((pipeline) => ({ connectionId: connectionRowId, pipeline })),
      )
      markSyncDataStale(queryClient, started)
      // Copy unchanged and deliberately so: "Sync started" names the button, not
      // one of its halves, and stays true now that the button moves both. The
      // jobs are asynchronous, so "started" is still all that can be claimed.
      // Shown before the refetch below so it answers the click at once.
      toast.info('Sync started')
      // Refetch ONLY the connection list, and return the promise so
      // `isPending` spans until the row carrying `queued` is rendered — the
      // screens OR `isPending` into their busy state for exactly that gap.
      // The data caches of both halves (transactions, accounts, Balance
      // Import, KPIs — DCP-397's union) were only marked stale above;
      // `useLedgerSyncCompletionWatcher` refreshes them when each run
      // finishes. Refetching them here re-read pre-sync rows.
      return queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBankConnections })
    },
    onError: (error, connectionRowId) => {
      // debug: bank-connection flow trace (DCP-324)
      lsDebugError('sync-connection:error', {
        connectionId: connectionRowId,
        error: error instanceof Error ? error.message : String(error),
      })
      toast.error(error instanceof Error ? error.message : 'Failed to start sync')
    },
  })
}
